import { get } from 'svelte/store';
import { home_data } from 'src/stores/account/user';
import { save_home } from 'src/helpers/home_helpers';

/**
 * Camera Monitoring Dashboard Settings
 * Persisted via home_data (interface/home/save endpoint)
 */

/**
 * Grid layout for the camera grid.
 * - `1x1`–`4x4`: uniform grids with N columns.
 * - `focus`: spotlight layout — one large camera spanning a 2x2 area with up to
 *   5 smaller cameras filling the rest of a 3x3 grid.
 */
export type GridLayout = '1x1' | '2x2' | '3x3' | '4x4' | 'focus';

export interface CameraMonitoringSettings {
	/** Preferred grid layout (columns) */
	preferredLayout: GridLayout;
	/** Whether to hide offline cameras from the grid */
	hideOfflineCameras: boolean;
	/** Ordered array of selected camera device IDs (order = display order, presence = selected) */
	cameras: string[];
	/** Device id of the spotlighted camera in the `focus` layout (empty = first camera) */
	focusedCameraId: string;
}

/** Default settings for camera monitoring dashboard */
export const defaultCameraMonitoringSettings: CameraMonitoringSettings = {
	preferredLayout: '2x2',
	hideOfflineCameras: false,
	cameras: [], // Empty = show all cameras in default order
	focusedCameraId: '',
};

/** Key used in home_data store */
const CAMERA_MONITORING_KEY = 'cameraMonitoring';

/**
 * Get camera monitoring settings from home_data
 * Falls back to defaults if not set
 */
/**
 * Normalize cameras value to array
 * Backend may serialize arrays as objects with numeric keys: {"0": "id1", "1": "id2"}
 */
function normalizeCamerasArray(cameras: unknown): string[] {
	if (Array.isArray(cameras)) {
		return cameras;
	}
	if (cameras && typeof cameras === 'object') {
		// Convert object with numeric keys back to array
		return Object.values(cameras as Record<string, string>);
	}
	return [];
}

export function getCameraMonitoringSettings(homeData): CameraMonitoringSettings {
	const stored = homeData?.[CAMERA_MONITORING_KEY];

	if (!stored) {
		return { ...defaultCameraMonitoringSettings };
	}

	// Merge with defaults to ensure all fields exist (handles schema evolution)
	return {
		...defaultCameraMonitoringSettings,
		...stored,
		// Ensure cameras is always an array (handles backend object serialization)
		cameras: normalizeCamerasArray(stored.cameras),
	};
}

/**
 * Save camera monitoring settings to home_data
 * Persists to backend via interface/home/save
 */
export async function saveCameraMonitoringSettings(
	settings: Partial<CameraMonitoringSettings>,
): Promise<boolean> {
	const current = getCameraMonitoringSettings(get(home_data));
	const updated: CameraMonitoringSettings = {
		...current,
		...settings,
	};

	const res = await save_home(CAMERA_MONITORING_KEY, updated);
	return res?.isok ?? false;
}

/**
 * Update a single setting and persist
 */
export async function updateCameraMonitoringSetting<K extends keyof CameraMonitoringSettings>(
	key: K,
	value: CameraMonitoringSettings[K],
): Promise<boolean> {
	return saveCameraMonitoringSettings({ [key]: value });
}

/**
 * Reset camera monitoring settings to defaults
 */
export async function resetCameraMonitoringSettings(): Promise<boolean> {
	return saveCameraMonitoringSettings(defaultCameraMonitoringSettings);
}
